import { useCurrentEnv } from '@e2m-customer-portal/hooks';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { root } from 'core/routes';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AppLogoIcon } from '../../assets/icons';

interface HeaderLogoProps {
  sidebarIsExpanded: boolean;
}

export function HeaderLogo({ sidebarIsExpanded }: Readonly<HeaderLogoProps>): ReactElement<HeaderLogoProps> {
  const { t } = useTranslation();
  const env = useCurrentEnv();
  const [logoSize, setLogoSize] = useState(0);

  const updateLogoSize = useCallback(() => {
    setLogoSize(sidebarIsExpanded ? 0 : document.documentElement.scrollTop / 2.5);
  }, [sidebarIsExpanded]);

  useEffect(() => {
    updateLogoSize();
    document.addEventListener('scroll', updateLogoSize);
    return () => document.removeEventListener('scroll', updateLogoSize);
  }, [updateLogoSize]);

  return (
    <div
      className="box-border flex shrink-0 items-center border-0 border-solid border-grey-100 md:w-[18.75rem] md:border-r"
      data-testid="header-logo"
    >
      {!sidebarIsExpanded && (
        <div
          // This is some magic number voodoo to have a nice grey border next to the shrunk logo
          className="absolute left-0 box-border w-12 border-0 border-r border-grey-100 md:border-solid pointer-events-none"
          style={{
            height: `${Math.min(64, Math.max(0, logoSize - 32.5) * 2.5)}px`,
            top: `${64 - Math.min(64, Math.max(0, logoSize - 32.5) * 2.5)}px`,
          }}
        />
      )}
      <Link aria-hidden to={root.getLink()} target="_top">
        <AppLogoIcon
          className="hidden text-green-200 md:block"
          style={{
            marginLeft: `${16 - Math.min(6, logoSize)}px`,
            marginRight: `${10 + Math.min(6, logoSize)}px`,
            width: `${73 - Math.min(44, logoSize)}px`,
          }}
        />
      </Link>
      <div className="hidden items-baseline gap-1 md:flex">
        <Link className="no-underline text-current" to={root.getLink()} target="_top">
          <Typography variant="h5" className="overflow-visible whitespace-pre-line" noWrap>
            {t('HEADER_CUSTOMER_PORTAL')}
          </Typography>
        </Link>
        {env !== 'PROD' && (
          <div
            className={clsx('rounded-sm p-1 text-sm leading-3 text-white', {
              'bg-rainbow opacity-80': env === 'LOCAL',
              'bg-[#1047c8]': env === 'DEV',
              'bg-[#ff861d]': env === 'STAGE',
            })}
          >
            {env}
          </div>
        )}
      </div>
      {/* Breakpoint marker for responsive development purposes */}
      {(env === 'LOCAL' || env === 'DEV') && (
        <div className="absolute left-0 top-0 select-none rounded-br-md border-0 border-b border-r border-dotted border-grey-400 bg-black/5 px-1 py-0.5 text-xs text-grey-400 backdrop-blur transition-opacity hover:opacity-0">
          <span className="sm:hidden">XS</span>
          <span className="hidden sm:block md:hidden">SM</span>
          <span className="hidden md:block lg:hidden">MD</span>
          <span className="hidden lg:block xl:hidden">LG</span>
          <span className="hidden xl:block 2xl:hidden">XL</span>
          <span className="hidden 2xl:block">2XL</span>
        </div>
      )}
    </div>
  );
}
